/**
 * Remove Empty Lines Tool
 *
 * Strips blank and whitespace-only lines from text.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const RemoveEmptyLinesTool: ITransformerTool = {
  id: 'remove-empty-lines',
  name: 'Remove Empty Lines',
  description: 'Strip blank and whitespace-only lines from text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['remove', 'empty', 'blank', 'lines', 'strip', 'whitespace', 'text'],

  seo: {
    slug: 'remove-empty-lines',
    pageTitle: 'Remove Empty Lines - Free Online Tool | SWE DevTools',
    metaDescription: 'Remove blank and whitespace-only lines from text instantly. Optionally trim whitespace from remaining lines. Fast, private, runs in your browser.',
    keywords: ['remove empty lines', 'remove blank lines', 'strip empty lines online', 'clean text lines'],
    relatedTools: ['deduplicate-lines', 'sort-lines', 'line-count'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'removeWhitespaceOnly',
      type: 'boolean',
      label: 'Remove Whitespace-Only Lines',
      defaultValue: true,
      description: 'Also remove lines that contain only spaces or tabs',
    },
    {
      name: 'trimLines',
      type: 'boolean',
      label: 'Trim Lines',
      defaultValue: false,
      description: 'Trim leading/trailing whitespace from remaining lines',
    },
    {
      name: 'collapseMultiple',
      type: 'boolean',
      label: 'Collapse Multiple Blanks',
      defaultValue: false,
      description: 'Replace consecutive blank lines with a single blank line instead of removing all',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const removeWhitespaceOnly = config.removeWhitespaceOnly !== false;
    const trimLines = Boolean(config.trimLines);
    const collapseMultiple = Boolean(config.collapseMultiple);

    const lines = input.split('\n');

    if (collapseMultiple) {
      const result: string[] = [];
      let prevBlank = false;

      for (const line of lines) {
        const isEmpty = removeWhitespaceOnly ? line.trim() === '' : line === '';
        if (isEmpty) {
          if (!prevBlank) {
            result.push('');
            prevBlank = true;
          }
        } else {
          result.push(trimLines ? line.trim() : line);
          prevBlank = false;
        }
      }
      return result.join('\n');
    }

    const result = lines.filter((line) => {
      if (removeWhitespaceOnly) return line.trim() !== '';
      return line !== '';
    });

    if (trimLines) {
      return result.map((line) => line.trim()).join('\n');
    }

    return result.join('\n');
  },
};
